import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, LogOut, Instagram, Phone, UserRound } from "lucide-react";
import { Logo } from "./Logo";
import { LogoMark } from "./LogoMark";
import { scrollToSection, startLenis, stopLenis } from "@/lib/lenis";
import { useAuth } from "@/context/AuthContext";
import AccountMenu from "./AccountMenu";

const LINKS = [
  { id: "story", label: "Story" },
  { id: "wisdom", label: "Wisdom" },
  { id: "impact", label: "Impact" },
  { id: "farm", label: "Farm" },
  { id: "products", label: "Products" },
  { id: "leadership", label: "Leadership" },
  { id: "visit", label: "Visit" },
  { id: "contact", label: "Contact" },
];

const INSTAGRAM_URL = "https://www.instagram.com/adveshta.cow";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, loading: authLoading, signOut, openAuth } = useAuth();

  const signIn = () => {
    setOpen(false);
    openAuth("login");
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (open) stopLenis();
    else startLenis();
  }, [open]);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToSection(id), open ? 150 : 0);
  };

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled
            ? "bg-cream/85 backdrop-blur-xl border-b border-sand/70"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
          <button
            data-testid="nav-logo-button"
            onClick={() => go("top")}
            className="cursor-pointer"
            aria-label="Adveshta Milk home"
          >
            <Logo />
          </button>

          <nav className="hidden xl:flex items-center gap-7">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="relative font-mono text-[11px] uppercase tracking-[0.24em] text-coal/70 hover:text-pine transition-colors duration-300 after:absolute after:left-0 after:-bottom-1.5 after:h-px after:w-full after:bg-gold after:scale-x-0 after:origin-left hover:after:scale-x-100 after:transition-transform after:duration-300"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              data-testid="nav-instagram-link"
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noreferrer"
              aria-label="Adveshta on Instagram — @adveshta.cow"
              className="hidden sm:inline-flex items-center justify-center h-11 w-11 rounded-full border border-pine/25 text-pine hover:bg-pine hover:text-cream transition-colors duration-300"
            >
              <Instagram className="h-4 w-4" />
            </a>
            <button
              data-testid="nav-enquire-button"
              onClick={() => go("contact")}
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-pine text-cream font-mono text-[11px] uppercase tracking-[0.22em] px-6 py-3 hover:bg-royal transition-colors duration-300"
            >
              Enquire
            </button>
            {authLoading ? (
              <div
                data-testid="nav-auth-loading"
                className="hidden sm:block h-11 w-11 rounded-full bg-sand/60 animate-pulse"
              />
            ) : user ? (
              <AccountMenu />
            ) : (
              <button
                data-testid="nav-signin-button"
                onClick={signIn}
                className="hidden sm:inline-flex items-center gap-2.5 rounded-full border border-pine/30 bg-white px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-pine hover:border-pine hover:bg-pine hover:text-cream transition-colors duration-300"
              >
                <UserRound className="h-4 w-4" /> Sign in
              </button>
            )}
            <button
              data-testid="nav-menu-button"
              onClick={() => setOpen(true)}
              className="xl:hidden inline-flex items-center justify-center h-11 w-11 rounded-full border border-pine/25 text-pine"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-overlay"
            initial={{ opacity: 0, y: -24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[60] bg-ink text-cream flex flex-col"
          >
            <div className="px-6 h-[72px] flex items-center justify-between">
              <Logo tone="light" />
              <button
                data-testid="nav-close-button"
                onClick={() => setOpen(false)}
                className="inline-flex items-center justify-center h-11 w-11 rounded-full border border-cream/25"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.45 }}
                  onClick={() => go(l.id)}
                  className="text-left font-serif text-3xl md:text-4xl py-1.5 border-b border-cream/10 hover:text-gold transition-colors duration-300"
                >
                  {l.label}
                </motion.button>
              ))}
            </nav>
            <div className="px-8 pb-6 flex flex-wrap items-center gap-x-8 gap-y-3">
              <a
                data-testid="nav-mobile-phone-link"
                href="tel:+918141261616"
                className="inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.22em] text-gold hover:text-cream transition-colors duration-300"
              >
                <Phone className="h-4 w-4" /> +91 81412 61616
              </a>
              <a
                data-testid="nav-mobile-instagram-link"
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.22em] text-gold hover:text-cream transition-colors duration-300"
              >
                <Instagram className="h-4 w-4" /> @adveshta.cow
              </a>
            </div>
            <div className="px-8 pb-8">
              {!authLoading &&
                (user ? (
                  <div className="flex items-center justify-between gap-3 rounded-2xl border border-cream/15 bg-pine/30 px-5 py-4">
                    <div className="flex items-center gap-3 min-w-0">
                      {user.picture ? (
                        <img
                          src={user.picture}
                          alt=""
                          referrerPolicy="no-referrer"
                          className="h-10 w-10 rounded-full object-cover ring-2 ring-gold/60 shrink-0"
                        />
                      ) : (
                        <span className="inline-flex h-10 w-10 items-center justify-center rounded-full ring-2 ring-gold/60 shrink-0">
                          <LogoMark className="h-6 w-6" tone="light" />
                        </span>
                      )}
                      <div className="min-w-0">
                        <p className="font-serif text-lg leading-tight truncate">{user.name}</p>
                        <p className="text-xs text-cream/50 truncate">{user.email}</p>
                      </div>
                    </div>
                    <button
                      data-testid="nav-mobile-signout-button"
                      onClick={() => signOut()}
                      className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-cream/25 hover:bg-cream/10 transition-colors duration-300 shrink-0"
                      aria-label="Sign out"
                    >
                      <LogOut className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    data-testid="nav-mobile-signin-button"
                    onClick={signIn}
                    className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-cream text-pine px-6 py-4 font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-gold transition-colors duration-300"
                  >
                    <UserRound className="h-4 w-4" /> Sign in / Create account
                  </button>
                ))}
            </div>
            <p className="px-8 pb-10 font-mono text-[10px] uppercase tracking-[0.3em] text-cream/50">
              Adveshta Projects Private Limited · Since 2022 · Gift City, Gandhinagar
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
